import React, { useEffect, useRef, useState } from 'react';
import { faGear } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useFeatureToggles } from '../../feature-toggles';
import { ColorPalette } from '../config';
import { IconButton } from '../icon-button/icon-button';
import { TrashIcon } from '../icons/trash-icon/trash-icon';
import { TextField } from '../text-field';
import { Tooltip } from '../tooltip';
import { PromptSettings } from './settings/prompt-settings';
import { PromptExecutionSettingsDto } from '../../api-service/clients';
import { AssistantType } from './assistant-type';
import { ChatStandardQuestionSelect } from './chat-standard-question-select';
import { LoadingIndicator } from '../loading-indicator';
import { ChatSendButton } from './chat-send-button';

export interface IChatMessageInputProps {
  isLoading: boolean;
  settings: PromptExecutionSettingsDto;
  assistantType: AssistantType;
  onSubmitPrompt: (chatInput: string, standardQuestionId?: string, isDraftStandardQuestion?: boolean) => void;
  onClearChat: () => void;
  onSettingsChange: (property: keyof PromptExecutionSettingsDto, value: any) => void;
  dataSourcesComponent?: React.ReactNode;
  // Given, it replaces the prompt settings with its own panel, shown right below the question field.
  settingsPanel?: { title: string; content: React.ReactNode };
  showDrafts?: boolean;
  // A new object each time a question is handed back, so the same question handed back twice still refills the field.
  returnedQuestion?: { question: string };
}

const settingsPanelId = 'chat-settings-panel';
const settingsPanelTitleId = 'chat-settings-panel-title';

const useStyles = makeStyles()({
  loadingIndicator: {
    width: '100%',
  },
});

export function ChatMessageInput(props: IChatMessageInputProps) {
  const { onSubmitPrompt, isLoading, onClearChat, onSettingsChange, settings, assistantType, dataSourcesComponent, settingsPanel, showDrafts, returnedQuestion } = props;
  const { classes } = useStyles();

  const { isFeatureEnabled } = useFeatureToggles();

  const isConfigMenuEnabled = isFeatureEnabled('AiSearchAllowCustomPromptSettings') || !!settingsPanel;

  const inputRef = useRef<HTMLInputElement | null>(null);

  const [inputString, setInputString] = useState('');
  const [showConfig, setShowConfig] = useState(false);

  useEffect(() => {
    // Ensure the input is focused only after the drawer is open and the component is fully mounted
    if (inputRef.current) {
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus();
      }, 300);

      return () => clearTimeout(timeoutId);
    }
    return () => {};
  }, []);

  useEffect(() => {
    if (returnedQuestion) {
      setInputString(returnedQuestion.question);
    }
  }, [returnedQuestion]);

  // standardQuestionId is an argument, not dropdown state, so a following free-text prompt cannot inherit it
  const handleSubmitPrompt = (value?: string, standardQuestionId?: string, isDraftStandardQuestion?: boolean) => {
    if ((!value && !inputString) || isLoading) return;
    onSubmitPrompt(value ?? inputString, standardQuestionId, isDraftStandardQuestion);
    showConfig && setShowConfig(false);
    setInputString('');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      handleSubmitPrompt();
    }
  };

  const onStandardQuestionChange = (value: string, standardQuestionId?: string, isDraftStandardQuestion?: boolean) => {
    setInputString(value);
    handleSubmitPrompt(value, standardQuestionId, isDraftStandardQuestion);
  };

  function getSettingsButtonLabel() {
    return showConfig ? 'Skjul indstillinger' : 'Vis indstillinger';
  }

  return (
    <Box sx={{ width: '100%', backgroundColor: 'white', display: 'flex', flexDirection: 'column', boxShadow: '0 -2px 10px rgba(0,0,0,0.2)' }}>
      <Box sx={{ color: ColorPalette.SecondaryTextColor, fontSize: '.650rem', p: 1, whiteSpace: 'pre-line', textAlign: 'right' }}>
        Svarene i denne chat er skabt af en generativ AI. Vær kritisk i brugen af svarene
      </Box>
      <Box sx={{ ml: 2, mb: 2, color: ColorPalette.SecondaryTextColor }}>
        <Box>Svar baseres på valgte datakilder</Box>
        {dataSourcesComponent}
      </Box>
      <ChatStandardQuestionSelect onStandardQuestionChange={onStandardQuestionChange} isLoading={isLoading} assistantType={assistantType} showDrafts={showDrafts} />
      <Box sx={{ display: 'flex', ml: 2, mb: 2, alignItems: 'center' }}>
        <LoadingIndicator loading={isLoading} className={classes.loadingIndicator}>
          <div style={{ width: '100%' }}>
            <TextField
              inputRef={inputRef}
              style={{ backgroundColor: ColorPalette.WHITE }}
              readOnly={isLoading}
              label="Stil et spørgsmål"
              variant="outlined"
              value={inputString}
              onChange={e => setInputString(e.target.value.replace(/^[\r\n]+/, ''))}
              onKeyDown={handleKeyDown}
            />
          </div>
        </LoadingIndicator>
        <Box>
          <ChatSendButton isSending={isLoading} disabled={isLoading || !inputString} onClick={() => handleSubmitPrompt()} />
        </Box>
      </Box>
      {settingsPanel && showConfig && (
        <Box component="section" id={settingsPanelId} aria-labelledby={settingsPanelTitleId} sx={{ ml: 2, mr: 2, mb: 1 }}>
          <Box component="h3" id={settingsPanelTitleId} sx={{ mt: 0 }}>
            {settingsPanel.title}
          </Box>
          {settingsPanel.content}
        </Box>
      )}
      <Box sx={{ marginLeft: '5px', marginBottom: '5px' }}>
        <Tooltip title="Ryd chat">
          <IconButton onClick={onClearChat} aria-label="Ryd chat" style={{ right: '0px' }} size="small">
            <TrashIcon />
          </IconButton>
        </Tooltip>
        {isConfigMenuEnabled && (
          <Tooltip title={getSettingsButtonLabel()}>
            <IconButton
              onClick={() => setShowConfig(!showConfig)}
              aria-label={getSettingsButtonLabel()}
              aria-controls={settingsPanel && showConfig ? settingsPanelId : undefined}
              aria-expanded={settingsPanel ? showConfig : undefined}
              size="small"
            >
              <FontAwesomeIcon icon={faGear} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {!settingsPanel && showConfig && (
        <Box sx={{ color: ColorPalette.SecondaryTextColor, fontSize: '.850rem', pl: 1, pr: 1, pb: 1, whiteSpace: 'pre-line' }}>
          <Box sx={{ marginBottom: 2 }}>
            <h3>Promptindstillinger</h3>
          </Box>
          <PromptSettings promptSettings={settings} onUpdateSettings={onSettingsChange} showRagDocuments={assistantType === AssistantType.Fasit} />
        </Box>
      )}
    </Box>
  );
}
